import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Button } from "./button";

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetPortal = DialogPrimitive.Portal;
export const SheetClose = DialogPrimitive.Close;

export const SheetOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn("ui-sheet-overlay", className)} {...props} />
));

SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const SheetContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showClose?: boolean;
    closeDisabled?: boolean;
    variant?: "default" | "detail";
  }
>(({ className, children, showClose = true, closeDisabled = false, variant = "default", ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn("ui-sheet-content", variant === "detail" && "ui-sheet-content-detail", className)}
      {...props}
    >
      {showClose && (
        <DialogPrimitive.Close asChild>
          <Button variant="secondary" size="icon" className="ui-sheet-close" type="button" aria-label="Close" disabled={closeDisabled}>
            <X size={20} />
          </Button>
        </DialogPrimitive.Close>
      )}
      {children}
    </DialogPrimitive.Content>
  </SheetPortal>
));

SheetContent.displayName = DialogPrimitive.Content.displayName;

export function SheetHeader({ className, ...props }: ComponentPropsWithoutRef<"div">) {
  return <div className={cn("ui-sheet-header", className)} {...props} />;
}

export function SheetBody({ className, ...props }: ComponentPropsWithoutRef<"div">) {
  return <div className={cn("ui-sheet-body", className)} {...props} />;
}

type SheetTitleProps = Omit<ComponentPropsWithoutRef<"div">, "children"> & { children: ReactNode };

export function SheetTitle({ children, className, ...props }: SheetTitleProps) {
  return (
    <div className={cn("ui-sheet-title-block", className)} {...props}>
      <DialogPrimitive.Title>{children}</DialogPrimitive.Title>
    </div>
  );
}

export const SheetDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("ui-sheet-description", className)} {...props} />
));

SheetDescription.displayName = DialogPrimitive.Description.displayName;
